import { useCallback, useMemo, useState } from "react"
import { useQuery } from "urql"

import "../graphql/vendor.query"

import { VENDOR_OPTIONS_LIMIT } from "../constants"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { GetInvoiceVendorOptionsQuery, GetInvoiceVendorOptionsQueryVariables } from "@/graphql/codegen/graphql"
import { GetInvoiceVendorOptionsDocument } from "@/graphql/codegen/graphql"
import { AddressFormat } from "@/types/common"
import { VENDOR_COMPANY_TYPES } from "@/types/companyType"
import useDebounce from "@/utils/useDebounce"
import { formatLocationAddress } from "@/utils/utils"

export interface VendorOption {
  id: string
  name: string
  aerosNumber: string
  as400Number: string
  accountType: string
  billingAddress: string
  billingCountry: string
  defaultExpenseAccountId?: string
}

interface UseVendorOptionsReturn {
  vendors: VendorOption[]
  isFetching: boolean
  setSearchTerm: (term: string) => void
}

export const useVendorOptions = (): UseVendorOptionsReturn => {
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY_MS)

  const [{ data, fetching }] = useQuery<GetInvoiceVendorOptionsQuery, GetInvoiceVendorOptionsQueryVariables>({
    query: GetInvoiceVendorOptionsDocument,
    variables: {
      input: {
        filter: {
          types: [...VENDOR_COMPANY_TYPES],
          query: debouncedSearchTerm || undefined,
        },
        offset: 0,
        limit: VENDOR_OPTIONS_LIMIT,
      },
    },
  })

  const vendors = useMemo<VendorOption[]>(() => {
    if (data?.company.list.__typename !== "CompanyListSuccess") return []

    return data.company.list.companies.map((company) => {
      const billing = company.locationsAssociations?.find((association) => association.billing)?.billing
      return {
        id: company.companyId,
        name: company.name ?? "",
        aerosNumber: company.publicId ?? "",
        as400Number: company.externalSystemId ?? "",
        accountType: company.types?.[0] ?? "",
        billingAddress: formatLocationAddress(billing, AddressFormat.BillTo),
        billingCountry: billing?.countryIsoCode ?? "",
        defaultExpenseAccountId: company.defaultExpenseAccountId ?? undefined,
      }
    })
  }, [data])

  const handleSetSearchTerm = useCallback((term: string) => {
    setSearchTerm(term)
  }, [])

  return { vendors, isFetching: fetching, setSearchTerm: handleSetSearchTerm }
}
